import React, { FC, useEffect, useRef, useState } from 'react';

import { FieldValues, useForm } from 'react-hook-form';

import logger from '@common/log';
import { stripAccolades } from '@common/string';
import {
  postEnecoBeXapiSiteApiV1FormsByFormNameSmartbattery,
  postEnecoBeXapiSiteApiV1SmartEnergyOffer,
} from '@dc-be/client/services.gen';
import { SmartEnergyOfferRequest, SmartEnergyOfferResponseRow } from '@dc-be/client/types.gen';
import { SmartHomeBatteryRendering } from '@sitecore/types/SmartHomeBattery';
import { Box, Bucket, Button, Card, NotificationBox, ProgressIndicator, Stack } from '@sparky';
import { pushToDataLayer } from '@tracking';

import Step1 from './Step1';
import Step2 from './Step2';
import Step3 from './Step3';
import Step4, { ThankYouStep } from './Step4';

const SmartHomeBattery: FC<SmartHomeBatteryRendering> = ({ fields }) => {
  const [step, setStep] = useState(1);
  const [formState, setFormState] = useState('initialized');
  const [nextButtonLoading, setNextButtonLoading] = useState(false);
  const [offers, setOffers] = useState<SmartEnergyOfferResponseRow[]>([]);
  const [generalError, setGeneralError] = useState(false);
  const [paybackTimeError, setPaybackTimeError] = useState(false);
  const formMethodsContact = useForm();
  const formMethodsOffer = useForm();
  const formMethodsSituation = useForm();
  const { handleSubmit: handleSubmitContact, getValues: getValuesContact } = formMethodsContact;
  const { getValues: getValuesOffer, setValue: setValueOffer } = formMethodsOffer;
  const { trigger: triggerSituation, getValues: getValuesSituation } = formMethodsSituation;
  const cardRef = useRef<HTMLDivElement | null>(null);

  const onSubmitContact = async () => {
    setFormState('submitting');
    setGeneralError(false);
    const data = await postEnecoBeXapiSiteApiV1FormsByFormNameSmartbattery({
      body: mapValuesContact({ ...getValuesContact(), ...getValuesSituation(), ...getValuesOffer() }, offers),
      path: { formName: stripAccolades('smartBatterySimulation') },
    }).then(res => {
      return res;
    });
    if (data.response.ok) {
      setFormState('submitted');
      pushToDataLayer({
        event: 'smarthomebatttery_funnel_step_form_complete',
        component_name: 'SmartHomeBatttery',
        meta_data: {},
      });
    } else {
      setFormState('initialized');
      setGeneralError(true);
      logger.error('Wxd7AY', 'Failed to submit form', { data });
    }
  };

  const getOfferData = async () => {
    setGeneralError(false);
    setPaybackTimeError(false);
    const { response, data } = await postEnecoBeXapiSiteApiV1SmartEnergyOffer({
      body: mapValues(getValuesSituation()),
    }).then(res => {
      return res;
    });
    if (response.ok && data?.data?.offers && data?.data?.offers?.length > 0) {
      if (
        data.data.offers.some(offer => {
          const period =
            getValuesSituation('constructionYear') === '<10 jaar'
              ? offer.payBackPeriodTwentyOnePercent
              : offer.payBackPeriodSixPercent;
          return typeof period === 'number' && period >= 100;
        })
      ) {
        setPaybackTimeError(true);
        setOffers([]);
        return false;
      }

      setOffers(
        data.data.offers.sort((a: SmartEnergyOfferResponseRow, b: SmartEnergyOfferResponseRow) => {
          if (
            getValuesSituation('constructionYear') === '<10 jaar' &&
            a.payBackPeriodTwentyOnePercent &&
            b.payBackPeriodTwentyOnePercent &&
            a.payBackPeriodTwentyOnePercent !== b.payBackPeriodTwentyOnePercent
          ) {
            return a.payBackPeriodTwentyOnePercent - b.payBackPeriodTwentyOnePercent;
          } else if (
            getValuesSituation('constructionYear') !== '<10 jaar' &&
            a.payBackPeriodSixPercent &&
            b.payBackPeriodSixPercent &&
            a.payBackPeriodSixPercent !== b.payBackPeriodSixPercent
          ) {
            return a.payBackPeriodSixPercent - b.payBackPeriodSixPercent;
          } else if (a.savedVolume && b.savedVolume) {
            return b.savedVolume - a.savedVolume;
          } else {
            return 0;
          }
        }),
      );
      return true;
    } else {
      setGeneralError(true);
      logger.error('Wxd7AY', 'Failed to submit form', { data, response });
      return false;
    }
  };

  const changeStep = async (newStep: number) => {
    let allowNewStep = true;
    setGeneralError(false);
    setNextButtonLoading(true);
    if (step === 1 && newStep === 2 && !(await triggerSituation())) {
      allowNewStep = false;
    } else if (step < 3 && newStep === 3) {
      if (await triggerSituation()) {
        setValueOffer('storage', '0');
        allowNewStep = await getOfferData();
      } else {
        allowNewStep = false;
      }
    }
    setNextButtonLoading(false);
    if (allowNewStep) {
      setStep(newStep);
      pushToDataLayer({
        event: dataLayerSteps[newStep as keyof typeof dataLayerSteps],
        component_name: 'SmartHomeBatttery',
        meta_data: {},
      });
      cardRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  useEffect(() => {
    pushToDataLayer({
      event: 'smarthomebatttery_funnel_step_1_woonituatie',
      component_name: 'SmartHomeBatttery',
      meta_data: {},
    });
  }, []);

  return (
    <Card corners={{ initial: 'square', sm: 'rounded' }}>
      <Box ref={cardRef}>
        {formState !== 'submitted' && (
          <>
            <Bucket.Content>
              <Stack alignX="start">
                <ProgressIndicator>
                  {[...Array(4)].map((_, index) => {
                    const stepKey = `processIndicatorStep${index + 1}` as StepKey;
                    return (
                      <ProgressIndicator.Item
                        key={fields[stepKey].value}
                        isActive={step === index + 1}
                        isDisabled={step < index + 1}
                        as="span"
                        onClick={() => changeStep(index + 1)}>
                        {fields[stepKey].value}
                      </ProgressIndicator.Item>
                    );
                  })}
                </ProgressIndicator>
              </Stack>
              {step === 1 && <Step1 fields={fields} formMethods={formMethodsSituation} />}
              {step === 2 && <Step2 fields={fields} formMethods={formMethodsSituation} />}
              {step === 3 && (
                <Step3
                  fields={fields}
                  offers={offers}
                  constructionYear={getValuesSituation('constructionYear')}
                  formMethods={formMethodsOffer}
                />
              )}
              {step === 4 && <Step4 fields={fields} formMethods={formMethodsContact} />}
              {generalError && (
                <NotificationBox
                  title={fields.step1LivingSituationFormErrorTitle.value}
                  variant="error"
                  isAlert
                  text={fields.step1LivingSituationFormErrorText.value}
                />
              )}
              {paybackTimeError && (
                <NotificationBox
                  title={fields.step1LivingSituationFormPaybackTimeErrorTitle.value}
                  variant="chat"
                  isAlert
                  text={fields.step1LivingSituationFormPaybackTimeErrorText.value}
                />
              )}
            </Bucket.Content>
            <Bucket.Footer>
              <Stack.Item>
                <Stack direction={{ initial: 'column', md: 'row' }} alignX={step > 1 ? 'justify' : 'end'} gap="5">
                  {step > 1 && (
                    <Button
                      isDisabled={formState !== 'initialized'}
                      onClick={() => changeStep(step - 1)}
                      action="secondary">
                      {fields.previousButtonText.value}
                    </Button>
                  )}
                  {step <= 3 && (
                    <Button isLoading={nextButtonLoading} onClick={() => changeStep(step + 1)}>
                      {fields.nextButtonText.value}
                    </Button>
                  )}
                  {step === 4 && (
                    <Button isLoading={formState === 'submitting'} onClick={handleSubmitContact(onSubmitContact)}>
                      {fields.submitButtonText.value}
                    </Button>
                  )}
                </Stack>
              </Stack.Item>
            </Bucket.Footer>
          </>
        )}
        {formState === 'submitted' && <ThankYouStep fields={fields} />}
      </Box>
    </Card>
  );
};

const mapValuesContact = (values: FieldValues, offers: SmartEnergyOfferResponseRow[]) => {
  return {
    customerID: values.isCustomer ? values.customerID : '',
    firstName: values.firstName,
    lastName: values.lastName,
    emailAddress: values.emailAddress,
    OriginalSubmitEmail: values.emailAddress,
    telephone: values.telephone,
    zipCode: values.zipCode,
    constructionYear: values.constructionYear,
    hasPV: values.solarPanels === 'hasPV',
    wantsPV: values.solarPanels === 'wantsPV',
    annualProduction: values.solarPanels === 'noPV' ? '0' : values.annualProduction || '3500',
    annualConsumption: values.annualConsumption || '1500',
    hasElecHeating: values.hasElecHeating,
    hasSwimPool: values.hasSwimPool,
    hasHeatPump: values.hasHeatPump,
    hasEV: values.hasEV,
    inverterType: values.inverterType,
    futureCat: '',
    custCap: offers[values.storage]?.savedVolume,
    custMods: offers[values.storage]?.numberOfModules,
    prodHeight: offers[values.storage]?.height,
    prodWidth: offers[values.storage]?.width,
    prodDepth: offers[values.storage]?.depth,
    custCost:
      values.constructionYear === '<10 jaar'
        ? offers[values.storage]?.investmentTwentyOnePercent
        : offers[values.storage]?.investmentSixPercent,
    custSavings: offers[values.storage]?.yearlySaving,
    custROI:
      values.constructionYear === '<10 jaar'
        ? offers[values.storage]?.roiTwentyOnePercent
        : offers[values.storage]?.roiSixPercent,
    custConsumption: offers[values.storage]?.selfUsageOptimalisation,
    custReduction: offers[values.storage]?.capacityTarifReduction,
    custSteering: offers[values.storage]?.enecoSmartSavings,
    custYield:
      values.constructionYear === '<10 jaar'
        ? offers[values.storage]?.payBackPeriodTwentyOnePercent
        : offers[values.storage]?.payBackPeriodSixPercent,
    basicCap: offers[0]?.savedVolume,
    basicMods: offers[0]?.numberOfModules,
    basicCost:
      values.constructionYear === '<10 jaar' ? offers[0]?.investmentTwentyOnePercent : offers[0]?.investmentSixPercent,
    basicSavings: offers[0]?.yearlySaving,
    basicROI: values.constructionYear === '<10 jaar' ? offers[0]?.roiTwentyOnePercent : offers[0]?.roiSixPercent,
    basicConsumption: offers[0]?.selfUsageOptimalisation,
    basicReduction: offers[0]?.capacityTarifReduction,
    basicSteering: offers[0]?.enecoSmartSavings,
    basicYield:
      values.constructionYear === '<10 jaar'
        ? offers[0]?.payBackPeriodTwentyOnePercent
        : offers[0]?.payBackPeriodSixPercent,
    consent1: values.consent ? 'Yes' : 'No',
    consent2: values.consent2 ? 'Yes' : 'No',
    requestType: values.requestType,
    simDate: new Date().toDateString(),
  };
};

type StepKey = 'processIndicatorStep1' | 'processIndicatorStep2' | 'processIndicatorStep3' | 'processIndicatorStep4';

const dataLayerSteps = {
  1: 'smarthomebatttery_funnel_step_1_woonituatie',
  2: 'smarthomebatttery_funnel_step_2_vebruikersprofiel',
  3: 'smarthomebatttery_funnel_step_3_configuratie',
  4: 'smarthomebatttery_funnel_step_4_contactgegevens',
};

const mapValues = (values: FieldValues) => {
  const combinations: SmartEnergyOfferRequest['deviceCombination'] = [];
  if (values.hasEV) combinations.push('EvCharger');
  if (values.hasHeatPump) combinations.push('HeatPump');
  if (values.hasSwimPool) combinations.push('SwimmingPool');
  if (values.hasElecHeating) combinations.push('ElectricalHeating');
  return {
    injectionVolume: values.solarPanels === 'noPV' ? 0 : values.annualProduction ? +values.annualProduction : 3500,
    usageVolume: values.annualConsumption ? +values.annualConsumption : 1500,
    inverterType: values.inverterType !== 'singleNoIdea' ? values.inverterType : 'type 4,6',
    deviceCombination: combinations,
  };
};

export default SmartHomeBattery;
